import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Options } from 'focus-trap';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { State } from '®app/ngrx';
import { getItemMenuButtonSelector } from '®shared/focus';
import { ModalControlsSaveable } from '®shared/modals';

import { BrokenLinkReport } from './broken-link-report';
import {
  ReportBrokenLinkAction,
  ReportBrokenLinkCancelAction,
} from './report-broken-link.actions';
import {
  ReportBrokenLinkState,
  selectReportBrokenLinkState,
} from './report-broken-link.state';

export const DETAILS_MAX_LENGTH = 1000;
export const EMAIL_MAX_LENGTH = 500;
export const SCREEN_RESOLUTION_MAX_LENGTH = 100;
export const USER_AGENT_MAX_LENGTH = 1000;

@Component({
  selector: 'rl-report-broken-link-modal',
  templateUrl: './report-broken-link-modal.component.html',
})
export class ReportBrokenLinkModalComponent implements OnInit, OnDestroy {
  detailsMaxLength = DETAILS_MAX_LENGTH;
  emailMaxLength = EMAIL_MAX_LENGTH;
  reportBrokenLinkDetailsForm: FormGroup;
  @ViewChild('reportBrokenLinkModal', { static: true })
  public reportBrokenLinkModal: ModalDirective;

  private reportBrokenLinkStateSubscription: Subscription;
  state: ReportBrokenLinkState;
  modalControl: ModalControlsSaveable;

  constructor(
    private cd: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private store: Store<State>,
  ) {
    this.createForm();
  }

  ngOnInit() {
    this.reportBrokenLinkStateSubscription = this.store
      .select(selectReportBrokenLinkState)
      .subscribe(state => {
        this.state = state;
        if (state.modalControl) {
          this.modalControl = state.modalControl;
          const isShown = this.modalControl.isShown;
          if (isShown && !this.reportBrokenLinkModal.isShown) {
            this.reportBrokenLinkDetailsForm.reset();
            this.reportBrokenLinkModal.show();
          } else if (!isShown && this.reportBrokenLinkModal.isShown) {
            this.reportBrokenLinkModal.hide();
          }
        }
        this.cd.markForCheck();
      });
  }

  ngOnDestroy() {
    if (this.reportBrokenLinkStateSubscription) {
      this.reportBrokenLinkStateSubscription.unsubscribe();
    }
  }

  createForm() {
    const notWhiteSpaceValidator = Validators.pattern(/\S/);

    this.reportBrokenLinkDetailsForm = this.formBuilder.group(
      {
        details: [
          null,
          [notWhiteSpaceValidator, Validators.maxLength(this.detailsMaxLength)],
        ],
        email: [
          null,
          [Validators.email, Validators.maxLength(this.emailMaxLength)],
        ],
        anonymous: [false],
      },
      { updateOn: 'blur' },
    );
  }

  get formEmailId(): string {
    return 'report-broken-link-modal-form-details_email';
  }

  get formEmailErrorsId(): string {
    return `${this.formEmailId}__errors`;
  }

  get formDetailsId(): string {
    return 'report-broken-link-modal-form-details_textarea';
  }

  get formAnonymousId(): string {
    return 'report-broken-link-modal-form-details_anonymous';
  }

  get formDetailsErrorsId(): string {
    return `${this.formDetailsId}__errors`;
  }

  get formDetails() {
    return this.reportBrokenLinkDetailsForm.get('details');
  }

  get formEmail() {
    return this.reportBrokenLinkDetailsForm.get('email');
  }

  get formAnonymous() {
    return this.reportBrokenLinkDetailsForm.get('anonymous');
  }

  get focusTrapConfig(): Options {
    if (!this.state || !this.state.currentItem) {
      return {};
    }

    const setReturnFocus = getItemMenuButtonSelector(this.state.currentItem.id);
    return { setReturnFocus };
  }

  get reporterEmail(): string | null {
    if (this.userEmailAddress && !this.formAnonymous.value) {
      return this.userEmailAddress;
    }

    return this.formEmail.value || null;
  }

  onReportBrokenLink() {
    if (
      this.reportBrokenLinkDetailsForm.invalid ||
      this.state.modalControl.isSaving
    ) {
      return;
    }

    const brokenLinkReport = new BrokenLinkReport();
    brokenLinkReport.details = this.formDetails.value
      ? this.formDetails.value.trim()
      : null;
    brokenLinkReport.id = uuid();
    brokenLinkReport.item = this.state.currentItem;
    brokenLinkReport.reporter_email = this.reporterEmail;
    brokenLinkReport.screen_resolution = this.getScreenResolution();
    brokenLinkReport.user_agent = this.getUserAgent();

    this.store.dispatch(new ReportBrokenLinkAction({ brokenLinkReport }));
  }

  onHidden($event: ModalDirective) {
    // if there is a dismiss reason, it means modal was closed by an intentional user action e.g escape, backdrop click
    if ($event.dismissReason && this.state.currentItem) {
      this.dispatchReportBrokenLinkCancelAction();
    }
  }

  onCancel() {
    if (this.state.modalControl.isSaving) {
      return;
    }
    if (this.state.currentItem) {
      this.dispatchReportBrokenLinkCancelAction();
    }
  }

  dispatchReportBrokenLinkCancelAction() {
    this.store.dispatch(
      new ReportBrokenLinkCancelAction({
        itemId: this.state.currentItem.id,
        screenResolution: this.getScreenResolution(),
        userAgent: this.getUserAgent(),
      }),
    );
  }

  get userEmailAddress(): string | null {
    if (
      this.state.user &&
      this.state.user.profile &&
      this.state.user.profile.email
    ) {
      return this.state.user.profile.email;
    }
    return null;
  }

  getScreenResolution(): string | null {
    if (
      window &&
      window.screen &&
      window.screen.height &&
      window.screen.width
    ) {
      const screenResolution = `${window.screen.height}x${window.screen.width}`;
      if (screenResolution.length <= SCREEN_RESOLUTION_MAX_LENGTH) {
        return screenResolution;
      }
    }
    return null;
  }

  getUserAgent(): string | null {
    if (
      window &&
      window.navigator &&
      window.navigator.userAgent &&
      window.navigator.userAgent.length <= USER_AGENT_MAX_LENGTH
    ) {
      return window.navigator.userAgent;
    }
    return null;
  }
}
